import { z } from "zod";

// ---------------------------------------------------------------------------
// Shared sub-models
// ---------------------------------------------------------------------------

export const MemberOut = z.object({
  id: z.number(),
  telegram_id: z.number(),
  username: z.string().nullable(),
  first_name: z.string(),
});

// ---------------------------------------------------------------------------
// Response models
// ---------------------------------------------------------------------------

export const UserOut = z.object({
  id: z.number(),
  telegram_id: z.number(),
  username: z.string().nullable(),
  first_name: z.string(),
});

export const GroupOut = z.object({
  id: z.number(),
  telegram_chat_id: z.number(),
  title: z.string(),
  personal_balance: z.string(), // Decimal serialised as string in JSON
  pending_count: z.number(),
  currency: z.string(),
});

export const MeOut = z.object({
  user: UserOut,
  groups: z.array(GroupOut),
  total_balance: z.string(),
});

export const TransferOut = z.object({
  from_user: MemberOut,
  to_user: MemberOut,
  amount: z.string(),
});

export const BalanceRowOut = z.object({
  user: MemberOut,
  paid: z.string(),
  owed: z.string(),
  net: z.string(),
});

export const BalancesOut = z.object({
  rows: z.array(BalanceRowOut),
  plan: z.array(TransferOut),
  currency: z.string(),
});

export const ExpenseShareOut = z.object({
  user: MemberOut,
  amount: z.string(),
});

export const ExpenseOut = z.object({
  id: z.number(),
  kind: z.enum(["simple", "itemized", "settlement"]),
  payer: MemberOut,
  total: z.string(),
  description: z.string(),
  created_at: z.string(),
  shares: z.array(ExpenseShareOut),
  undoable: z.boolean(),
  currency: z.string(),
  source_receipt_id: z.string().nullable().optional(),
});

export const ReceiptItemOut = z.object({
  name: z.string(),
  price: z.string(),
});

export const ReceiptUploadOut = z.object({
  receipt_id: z.string(),  // UUID as string
  status: z.string(),
});

export const ReceiptStatus = z.enum(["parsing", "ready", "finalized", "failed", "expired"]);

export const ReceiptClaimantOut = z.object({
  id: z.number(),
  telegram_id: z.number(),
  username: z.string().nullable(),
  first_name: z.string(),
  portion: z.coerce.number().nullable().default(null),
});

export const ReceiptItemDetailOut = z.object({
  idx: z.number(),
  name: z.string(),
  price: z.string(),
  quantity: z.coerce.number().default(1),
  group: z.string().nullable().default(null),
  claimants: z.array(ReceiptClaimantOut),
  muted: z.boolean(),
  phantom_count: z.number(),
});

export const ReceiptShareOut = z.object({
  user_id: z.number(),
  first_name: z.string(),
  amount: z.string(),
});

export const ReceiptGuestOut = z.object({
  id: z.number(),
  label: z.string(),
  item_idxs: z.array(z.number()),
  subtotal: z.string().default("0"),
  service: z.string().default("0"),
  total: z.string().default("0"),
});

export const ReceiptDetailOut = z.object({
  id: z.string(),              // UUID as string
  chat_id: z.number(),
  status: ReceiptStatus,
  parse_error: z.string().nullable().optional(),
  uploader: ReceiptClaimantOut,
  payer: ReceiptClaimantOut.nullable(),
  description: z.string(),
  currency: z.string(),
  items: z.array(ReceiptItemDetailOut),
  tax: z.string(),
  tip: z.string(),
  tip_split_count: z.number(),
  per_person_tip: z.string().default("0"),
  service_charge: z.string().default("0"),
  service_pct: z.string().nullable().default(null),
  guests: z.array(ReceiptGuestOut).default([]),
  receipt_total: z.string(),
  total: z.string(),
  shares: z.array(ReceiptShareOut),
  expires_at: z.string(),
  expense_id: z.number().nullable(),
});

export const ChatRef = z.object({
  id: z.number(),
  telegram_chat_id: z.number(),
  title: z.string(),
});

export const PendingOut = z.object({
  token: z.string(),
  chat: ChatRef,
  action_type: z.string(),
  summary: z.string(),
  required_tg_ids: z.array(z.number()),
  approved_tg_ids: z.array(z.number()),
  sender_tg_id: z.number(),
  expires_at: z.string(),
  amount: z.string().nullable(),
  currency: z.string().nullable(),
  description: z.string().nullable(),
  actor_display: z.string().nullable(),
  receipt_id: z.string().nullable().optional(),  // present for receipt_settle actions
});

// ---------------------------------------------------------------------------
// RecordedOut discriminated union
// Mirrors the Python RecordedOut union in src/api/schemas.py
// ---------------------------------------------------------------------------

export const RecordedOut = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("expense_recorded"),
    description: z.string(),
    total: z.string(),
    // shares: list of [user_id, amount] tuples serialised as arrays
    shares: z.array(z.tuple([z.number(), z.string()])),
    // participants: { str(user_id): display_name }
    participants: z.record(z.string(), z.string()),
  }),
  z.object({
    kind: z.literal("settlement_recorded"),
    payer_name: z.string(),
    target_name: z.string(),
    amount: z.string(),
    description: z.string().nullable(),
  }),
  z.object({
    kind: z.literal("settle_all_recorded"),
    n_transfers: z.number(),
  }),
  z.object({
    kind: z.literal("receipt_recorded"),
    description: z.string(),
    total: z.string(),
    expense_id: z.number(),
  }),
  z.object({
    kind: z.literal("pending_created"),
    token: z.string(),
    summary: z.string(),
  }),
]);

// ---------------------------------------------------------------------------
// Inferred TypeScript types
// ---------------------------------------------------------------------------

export type UserOut = z.infer<typeof UserOut>;
export type GroupOut = z.infer<typeof GroupOut>;
export type MeOut = z.infer<typeof MeOut>;
export type MemberOut = z.infer<typeof MemberOut>;
export type TransferOut = z.infer<typeof TransferOut>;
export type BalanceRowOut = z.infer<typeof BalanceRowOut>;
export type BalancesOut = z.infer<typeof BalancesOut>;
export type ExpenseShareOut = z.infer<typeof ExpenseShareOut>;
export type ExpenseOut = z.infer<typeof ExpenseOut>;
export type ReceiptItemOut = z.infer<typeof ReceiptItemOut>;
export type ReceiptUploadOut = z.infer<typeof ReceiptUploadOut>;
export type ReceiptStatus = z.infer<typeof ReceiptStatus>;
export type ReceiptClaimantOut = z.infer<typeof ReceiptClaimantOut>;
export type ReceiptItemDetailOut = z.infer<typeof ReceiptItemDetailOut>;
export type ReceiptDetailOut = z.infer<typeof ReceiptDetailOut>;
export type ChatRef = z.infer<typeof ChatRef>;
export type PendingOut = z.infer<typeof PendingOut>;
export type RecordedOut = z.infer<typeof RecordedOut>;
